/* @flow */
import * as React from 'react';
import gql from 'graphql-tag';
import idx from 'idx';
import { showToast } from 'reducers/toasts';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';

import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import BoxHeader from 'components/BoxHeader';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import EventTracking from 'lib/EventTracking';
import Text from 'components/Text';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import { orgTypeString } from 'constants/SelfServeConstants';
import { priceInCents } from 'utils/selfServeHelpers';
import { STUDENT_COUNT_FOR_TWO_DAY_FEATURES } from 'admin/reducers/adminUpgradePage';
import { setStripeCallStatus, reinitialize, resetFromError } from 'reducers/creditCards';

type Props = {
  onRequestHide?: Function,
  requestInvoice: Function,
  showToast: Function,
  onAgreed: Function,
  reduxState: Object,
  organizationName: ?string,
  organizationId: number,
  loading: boolean,
  createAndChargeNewOrgCard: (
    stripeSourceId: string,
    orgUuid: string,
    orgId: number,
    amount: number,
    quoteUuid: string
  ) => Promise<*>,

  // credit card redux
  setStripeCallStatus: Function,
  reinitialize: Function,
  resetFromError: Function,
  cardState: {
    errorMessage: ?string,
    stripeCallStatus: ?string,
    sourceId: ?string,
  },
};

type State = {
  submitting: boolean,
};

class AdminUpgradeTermsModal extends React.Component<Props, State> {
  state: State = {
    submitting: false,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.cardState.stripeCallStatus === 'done' &&
      this.props.cardState.stripeCallStatus !== 'done'
    ) {
      this.handleCardCreationComplete(nextProps.cardState.sourceId);
    }
  }

  handleSubmit = () => {
    EventTracking.trackKey('admin_self_serve.terms_modal.agree.click');
    this.setState({ submitting: true });

    const { reduxState } = this.props;
    const actionNeeded = reduxState.quote.studentsCount < STUDENT_COUNT_FOR_TWO_DAY_FEATURES;

    if (reduxState.paymentMethod === 'card') {
      this.props.setStripeCallStatus('start');
    } else {
      this.props
        .requestInvoice()
        .then(data => {
          const error = idx(data, _ => _.data.sendQuoteInvoice.error);
          if (error) {
            this.props.showToast(error.message, 'error');
            this.setState({ submitting: false });
          } else {
            console.log(actionNeeded);
            this.handleSuccess(actionNeeded);
          }
        })
        .catch(e => {
          this.setState({ submitting: false });
          this.props.showToast('An error occurred while submitting the invoice request.', 'error');
          console.debug(e, e && e.stack);
          console.error('AdminUpgradeTermsModal mutation error');
        });
    }
  };

  handleClose = () => {
    EventTracking.trackKey('admin_self_serve.terms_modal.close.click');
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleSuccess = (actionNeeded: boolean) => {
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
    this.props.onAgreed(actionNeeded);
  };

  handleCardCreationComplete = (sourceId: ?string) => {
    const onError = error => {
      this.props.resetFromError(error);
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    };

    const {
      reduxState: {
        fields,
        quote: { total, uuid: quoteUuid },
      },
      cardState: { errorMessage },
      organizationId,
    } = this.props;

    if (sourceId) {
      this.props
        .createAndChargeNewOrgCard(
          sourceId,
          fields.get('organizationUuid'),
          organizationId,
          priceInCents(total),
          quoteUuid
        )
        .then(({ data: { createAndChargeNewOrgCard: resp } }) => {
          if (resp.error) {
            onError(resp.error.message);
          } else if (resp.declineReason) {
            onError(resp.declineReason);
          } else {
            // Reset cc state
            this.props.showToast('Your card has been charged. We’ll be in touch.', 'success');
            this.props.reinitialize();
            this.handleSuccess(false);
          }
        })
        .catch(e => {
          this.props.showToast(
            `There was an error adding your card. Please try again or email ${LinkConstants.SUPPORT_EMAIL} for help.`,
            'error'
          );
          console.debug(e, e && e.stack);
          console.error('AdminUpgradeTermsModal createAndChargeNewOrgCard mutation error');
        });
    } else {
      onError(errorMessage);
    }
  };

  render() {
    const { loading, reduxState, organizationName } = this.props;
    if (loading || !organizationName) {
      return null;
    }

    const orgType = reduxState.fields.get('orgType');
    const typeString = orgTypeString[orgType];
    const orgTypeForAgreement = orgType === 'district' ? 'district' : 'school';
    const linkForAgreement =
      orgTypeForAgreement === 'district'
        ? LinkConstants.DISTRICT_AGREEMENT
        : LinkConstants.SCHOOL_AGREEMENT;
    return (
      <Dialog size="medium" onRequestHide={this.handleClose} hideOnBlur={false}>
        <BoxHeader title={`Remind ${typeString} Plan Terms`} />
        <BoxBody divided>
          <Text element="p" align="left">
            I am legally authorized to purchase the {typeString} Plan on behalf of{' '}
            {organizationName}. I understand this order is bound by the terms of the{' '}
            <Link href={linkForAgreement} target="_blank">
              Remind {typeString} Plan Standard Agreement
            </Link>
            , and I have read Remind’s{' '}
            <Link href={LinkConstants.PRIVACY_POLICY} target="_blank">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link href={LinkConstants.TERMS_OF_SERVICE} target="_blank">
              Terms of Service
            </Link>
            .
          </Text>
          <Text element="p" align="left">
            I understand {organizationName} is responsible for complying with the Children’s Online
            Privacy Protection Act of 1998 (COPPA), including obtaining parental consent for
            collection of personal information in Remind.
          </Text>
        </BoxBody>
        <BoxFooter>
          <Button
            onClick={this.handleSubmit}
            disabled={this.state.submitting}
            loading={this.state.submitting}>
            I agree
          </Button>
        </BoxFooter>
      </Dialog>
    );
  }
}

const UPDATE_PHONE_CALL = gql`
  mutation SendQuoteInvoice($uuid: String!) {
    sendQuoteInvoice(uuid: $uuid) {
      error {
        message
      }
    }
  }
`;

const withRequestInvoiceMutation = graphql(UPDATE_PHONE_CALL, {
  props: ({ ownProps, mutate }) => ({
    requestInvoice: () => {
      return mutate({
        variables: { uuid: ownProps.reduxState.quote.uuid },
      });
    },
  }),
});

const withCreditCardMutation = graphql(
  gql`
    mutation createAndChargeNewOrgCard($input: CreateAndChargeNewOrgCardInput!) {
      createAndChargeNewOrgCard(input: $input) {
        declineReason
        error {
          message
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      createAndChargeNewOrgCard(
        stripeSourceId: string,
        orgUuid: string,
        orgId: number,
        amount: number,
        quoteUuid: string
      ) {
        const input = { stripeSourceId, orgUuid, orgId, amount, quoteUuid };
        return mutate({ variables: { input } });
      },
    }),
  }
);

const withData = graphql(
  gql`
    query AdminTerms($uuid: String!) {
      organizations(uuids: [$uuid]) {
        uuid
        id
        name
      }
    }
  `,
  {
    options: ({ reduxState }) => {
      return {
        variables: {
          uuid: reduxState.fields.get('organizationUuid'),
        },
      };
    },
    props: ({ data }) => {
      return {
        organizationName: data.organizations ? data.organizations[0].name : null,
        organizationId: data.organizations ? data.organizations[0].id : null,
        loading: data.loading,
      };
    },
  }
);

function mapStateToProps(state) {
  return {
    reduxState: state.adminUpgradePage,
    cardState: {
      errorMessage: state.creditCards.errorMessage,
      stripeCallStatus: state.creditCards.stripeCallStatus,
      sourceId: state.creditCards.sourceId,
    },
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators(
    { showToast, setStripeCallStatus, resetFromError, reinitialize },
    dispatch
  );
}
export { AdminUpgradeTermsModal };
export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRequestInvoiceMutation,
  withCreditCardMutation,
  withData
)(AdminUpgradeTermsModal);
